import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, subtask, todo, type Todo } from "@listto/powersync";
import { computeNextDueDate, nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";

/**
 * Creates a new Todo.
 */
export async function createTodoMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  text: string,
  opts?: {
    listId?: string;
    important?: boolean;
    dueDate?: string;
    myDayDate?: string;
    note?: string;
    recurrenceRule?: string;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(todo).values({
    id,
    user_id: userId,
    list_id: opts?.listId ?? null,
    text: text.trim(),
    completed: 0,
    important: opts?.important ? 1 : 0,
    note: opts?.note ?? null,
    due_date: opts?.dueDate ?? null,
    my_day_date: opts?.myDayDate ?? null,
    recurrence_rule: opts?.recurrenceRule ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Updates an existing Todo.
 */
export async function updateTodoMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    text: string | null;
    completed: number | null;
    important: number | null;
    note: string | null;
    due_date: string | null;
    recurrence_rule: string | null;
    my_day_date: string | null;
    list_id: string | null;
    reminder_at: string | null;
  }>,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.text !== undefined && finalUpdates.text !== null) {
    finalUpdates.text = finalUpdates.text.trim();
  }

  await db
    .update(todo)
    .set({ ...finalUpdates, updated_at: nowISO() })
    .where(eq(todo.id, id));
}

/**
 * Toggles a Todo's completion status. Handles recurrence if necessary.
 */
export async function toggleTodoMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  currentCompleted: boolean,
  currentTodo?: Todo,
): Promise<void> {
  const completing = !currentCompleted;

  const recurrenceRule = currentTodo?.recurrence_rule;
  if (completing && recurrenceRule) {
    const nextDue = computeNextDueDate(currentTodo.due_date, recurrenceRule);
    const newId = generateUUID();
    const now = nowISO();

    await db.transaction(async (trx) => {
      await trx.update(todo).set({ completed: 1, updated_at: now }).where(eq(todo.id, id));

      await trx.insert(todo).values({
        id: newId,
        user_id: currentTodo.user_id,
        list_id: currentTodo.list_id ?? null,
        text: currentTodo.text,
        completed: 0,
        important: currentTodo.important,
        note: currentTodo.note ?? null,
        due_date: nextDue,
        my_day_date: null,
        recurrence_rule: recurrenceRule,
        created_at: now,
        updated_at: now,
      });
    });
  } else {
    await db
      .update(todo)
      .set({ completed: completing ? 1 : 0, updated_at: nowISO() })
      .where(eq(todo.id, id));
  }
}

/**
 * Toggles a Todo's important status.
 */
export async function toggleImportantMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  currentImportant: boolean,
): Promise<void> {
  await db
    .update(todo)
    .set({ important: currentImportant ? 0 : 1, updated_at: nowISO() })
    .where(eq(todo.id, id));
}

/**
 * Adds or removes a Todo from My Day.
 */
export async function addToMyDayMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  include: boolean,
): Promise<void> {
  await db
    .update(todo)
    .set({
      my_day_date: include ? nowISO() : null,
      updated_at: nowISO(),
    })
    .where(eq(todo.id, id));
}

/**
 * Deletes a Todo and its associated subtasks.
 */
export async function deleteTodoMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(subtask).where(eq(subtask.todo_id, id));
    await trx.delete(todo).where(eq(todo.id, id));
  });
}
